import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Waves, Calendar, Clock, Star, Phone, Mail, MapPin, Menu, X, ChevronRight, ChevronLeft,
  Award, Shield, Heart, Instagram, Facebook, Globe, MessageCircle, Check,
  Wind, Droplets, ArrowUp, Thermometer
} from "lucide-react";

// Default content fallback
const DEFAULT_CONTENT = {
  hero: { title_pt: "Aprende a Surfar", title_highlight_pt: "Com os Melhores", subtitle_pt: "A melhor escola de surf da região.", title_en: "Learn to Surf", title_highlight_en: "With the Best", subtitle_en: "The best surf school in the region." },
  about: { title_pt: "Por que aprender connosco?", text1_pt: "Instrutores certificados.", text2_pt: "Equipamento de qualidade.", title_en: "Why learn with us?", text1_en: "Certified instructors.", text2_en: "Quality equipment." },
  services: [],
  pricing: { lesson_price: 25, pack_5_price: 100, private_price: 40, monthly_price: 60, monthly_lessons_per_week: 2 },
  rentals: [],
  conditions: { title_pt: "Informações", title_en: "Information", items_pt: [], items_en: [], payment_methods_pt: "MB / MBWay", payment_methods_en: "MB / MBWay" },
  contact: { phone: "+351 961 410 360", email: "hi@apuliasurfschool.com", address: "Praia da Apúlia", address_maps_url: "", whatsapp: "+351961410360" },
  stats: { instructors: 6, boards: 30, lessons_per_week: 12, wetsuits: 70 }
};

// Wave animation CSS
const waveAnimationStyles = `
  @keyframes wave {
    0% { transform: translateX(0) translateZ(0) scaleY(1); }
    50% { transform: translateX(-25%) translateZ(0) scaleY(0.55); }
    100% { transform: translateX(-50%) translateZ(0) scaleY(1); }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  @keyframes float-slow {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
  }
  .float {
    animation: float 4s ease-in-out infinite;
    will-change: transform;
  }
  .float-slow {
    animation: float-slow 5s ease-in-out infinite;
    will-change: transform;
  }
  .float-delay {
    animation: float 4s ease-in-out 0.5s infinite;
    will-change: transform;
  }
  .wave {
    position: absolute;
    left: 0;
    width: 200%;
    height: 100%;
    background-repeat: repeat no-repeat;
    background-position: 0 bottom;
    transform-origin: center bottom;
  }
  .wave1 {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 88.7'%3E%3Cpath d='M800 56.9c-155.5 0-204.9-50-405.5-49.9-200 0-250 49.9-394.5 49.9v31.8h800v-.2-31.6z' fill='%23ffffff'/%3E%3C/svg%3E");
    background-size: 50% 100px;
    animation: wave 8s cubic-bezier(0.36, 0.45, 0.63, 0.53) infinite;
  }
  .wave2 {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 88.7'%3E%3Cpath d='M800 56.9c-155.5 0-204.9-50-405.5-49.9-200 0-250 49.9-394.5 49.9v31.8h800v-.2-31.6z' fill='%23f8f7f4'/%3E%3C/svg%3E");
    background-size: 50% 120px;
    animation: wave 10s cubic-bezier(0.36, 0.45, 0.63, 0.53) -0.125s infinite, wave 10s cubic-bezier(0.36, 0.45, 0.63, 0.53) -0.125s infinite;
    opacity: 0.5;
  }
  .card-wave-container {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    overflow: hidden;
  }
  .card-wave {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 200%;
    height: 50px;
    background-repeat: repeat-x;
    background-position: 0 bottom;
    background-size: 50% 50px;
  }
  .card-wave1 {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 50'%3E%3Cpath d='M0,25 Q200,0 400,25 T800,25 L800,50 L0,50 Z' fill='%23ffffff'/%3E%3C/svg%3E");
    animation: wave 10s linear infinite;
    opacity: 0.2;
  }
  .card-wave2 {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 50'%3E%3Cpath d='M0,30 Q200,5 400,30 T800,30 L800,50 L0,50 Z' fill='%23ffffff'/%3E%3C/svg%3E");
    animation: wave 8s linear infinite reverse;
    opacity: 0.15;
  }
`;

// Helper function to generate Google Maps embed URL from address or maps URL
const getGoogleMapsEmbedUrl = (mapsUrl, address) => {
  // Exact coordinates for Apúlia Surf School
  const defaultLat = 41.48366009932891;
  const defaultLng = -8.777443357672656;
  
  // Try to extract coordinates from the maps URL
  if (mapsUrl) {
    // Match patterns like @41.4847,-8.7812 or place/.../@41.4847,-8.7812
    const coordMatch = mapsUrl.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1500!2d${lng}!3d${lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM!5e0!3m2!1spt-PT!2spt!4v1`;
    }
  }
  
  // Use exact school coordinates with good zoom level (1500 = ~500m view)
  return `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1500!2d${defaultLng}!3d${defaultLat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM!5e0!3m2!1spt-PT!2spt!4v1`;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [language, setLanguage] = useState("pt");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [debugLink, setDebugLink] = useState(null);
  
  // Hero carousel - only real school photos
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroImages = [
    "/assets/images2/gallery/image1.jpeg",
    "/assets/images2/gallery/image2.jpeg",
    "/assets/images2/gallery/image3.jpeg",
    "/assets/images2/gallery/image4.jpeg",
    "/assets/images2/about/about2.jpeg"
  ];
  
  // Testimonials carousel
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  
  // Mensalidade carousel
  const [mensalidadeIndex, setMensalidadeIndex] = useState(0);
  
  // Dynamic content
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [services, setServices] = useState({ packs: [], mensalidades: [] });
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [googleRating, setGoogleRating] = useState(5.0);
  const [googleReviewCount, setGoogleReviewCount] = useState(291);
  
  // Weather/Sea conditions
  const [seaConditions, setSeaConditions] = useState(null);

  // Hero carousel auto-rotation (5 seconds per slide)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  useEffect(() => {
    fetchContent();
    fetchUpcomingLessons();
    fetchTestimonials();
    fetchSeaConditions();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content/landing_page`);
      setContent({ ...DEFAULT_CONTENT, ...res.data });
      // Set services from database
      if (res.data.packs || res.data.mensalidades) {
        setServices({
          packs: res.data.packs || [],
          mensalidades: res.data.mensalidades || []
        });
      }
    } catch (err) {
      console.error("Failed to fetch content:", err);
    }
  };

  const fetchUpcomingLessons = async () => {
    try {
      const res = await axios.get(`${API}/lessons/upcoming/public`);
      setUpcomingLessons(res.data || []);
    } catch {}
  };

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/testimonials`);
      setTestimonials(res.data?.testimonials || []);
      setGoogleRating(res.data?.google_rating || 5.0);
      setGoogleReviewCount(res.data?.google_review_count || 291);
    } catch {}
  };

  const fetchSeaConditions = async () => {
    try {
      const res = await axios.get(`${API}/weather`);
      setSeaConditions(res.data);
    } catch {}
  };

  const handleGetStarted = () => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : user.role === "professor" ? "/professor" : "/dashboard");
    } else {
      setShowAuth(true);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API}/auth/magic-link`, { email: email.trim(), name: name.trim() || undefined });
      setMagicLinkSent(true);
      if (res.data.debug_link) setDebugLink(res.data.debug_link);
    } catch (err) {
      alert(err.response?.data?.detail || "Erro");
    } finally {
      setAuthLoading(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const t = (ptText, enText) => language === "pt" ? ptText : enText;
  const c = content;

  const images = {
    hero: "/assets/images/background.jpeg",
    about: "/assets/images2/about/about2.jpeg",
    gallery1: "/assets/images2/gallery/image1.jpeg",
    gallery2: "/assets/images2/gallery/image2.jpeg",
    gallery3: "/assets/images2/gallery/image3.jpeg",
    gallery4: "/assets/images2/gallery/image4.jpeg",
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Inject wave animation styles */}
      <style>{waveAnimationStyles}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <img src="/assets/images/logo.png" alt="Apúlia Surf School" className="h-10 w-auto" />
            
            <div className="hidden md:flex items-center gap-6">
              {[
                { id: "lessons", label: t("Aulas", "Lessons") },
                { id: "pricing", label: t("Preços", "Pricing") },
                { id: "about", label: t("Sobre", "About") },
                { id: "contact", label: t("Contacto", "Contact") },
              ].map(item => (
                <button key={item.id} onClick={() => scrollToSection(item.id)} className="text-sm text-[#475569] hover:text-[#6697a4] transition-colors">
                  {item.label}
                </button>
              ))}
              <button onClick={() => setLanguage(language === "pt" ? "en" : "pt")} className="flex items-center gap-1 text-sm text-[#94A3B8] hover:text-[#6697a4]">
                <Globe className="h-4 w-4" /> {language.toUpperCase()}
              </button>
              <Button onClick={handleGetStarted} className="bg-[#6697a4] hover:bg-[#3d5a63] rounded-full px-6">
                {user ? t("Dashboard", "Dashboard") : t("Começar", "Get Started")}
              </Button>
            </div>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4 px-4 space-y-3">
            {[
              { id: "services", label: t("Serviços", "Services") },
              { id: "lessons", label: t("Aulas", "Lessons") },
              { id: "pricing", label: t("Preços", "Pricing") },
              { id: "about", label: t("Sobre", "About") },
              { id: "contact", label: t("Contacto", "Contact") },
            ].map(item => (
              <button key={item.id} onClick={() => scrollToSection(item.id)} className="block w-full text-left py-2 text-[#475569]">
                {item.label}
              </button>
            ))}
            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <button onClick={() => setLanguage(language === "pt" ? "en" : "pt")} className="text-sm text-[#94A3B8]">
                {language === "pt" ? "English" : "Português"}
              </button>
              <Button onClick={handleGetStarted} size="sm" className="bg-[#6697a4] hover:bg-[#3d5a63] rounded-full">
                {user ? t("Dashboard", "Dashboard") : t("Começar", "Start")}
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section with Photo Carousel Background and Wave Animation */}
      <section className="relative min-h-[100svh] flex items-center pt-16 overflow-hidden">
        {/* Photo Carousel Background */}
        <div className="absolute inset-0">
          {heroImages.map((img, idx) => (
            <img 
              key={`hero-slide-${idx}`}
              src={img} 
              alt={`Slide ${idx + 1}`}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
              style={{
                opacity: idx === currentSlide ? 1 : 0,
                zIndex: idx === currentSlide ? 1 : 0
              }}
            />
          ))}
          {/* Darker brand-aligned gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0d3347]/90 via-[#1E5F83]/75 to-[#f97316]/20 z-10" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="max-w-xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white/95 mb-4 leading-tight">
              {c.hero?.[`title_${language}`] || c.hero?.title_pt}
              <span className="block text-[#fcd9a8]">{c.hero?.[`title_highlight_${language}`] || c.hero?.title_highlight_pt}</span>
            </h1>
            <p className="text-base sm:text-lg text-white/75 mb-8 leading-relaxed">
              {c.hero?.[`subtitle_${language}`] || c.hero?.subtitle_pt}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={handleGetStarted} size="lg" className="bg-[#e8927c] hover:bg-[#d4806b] text-white rounded-full px-8 shadow-md hover:shadow-lg transition-all">
                {t("Reservar Aula", "Book Lesson")} <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <a href={`https://wa.me/${c.contact?.whatsapp?.replace(/\D/g, "") || c.contact?.phone?.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="border-white/40 text-white/90 hover:bg-white/10 rounded-full px-8 w-full sm:w-auto backdrop-blur-sm">
                  <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp
                </Button>
              </a>
            </div>
          </div>

          {/* Sea Conditions Widget */}
          {seaConditions && (
            <div className="mt-10 bg-white/15 backdrop-blur-md rounded-2xl p-4 sm:p-6 max-w-md border border-white/20">
              <div className="flex items-center gap-2 mb-3">
                <Waves className="h-5 w-5 text-[#fcd9a8]" />
                <span className="text-white/90 font-medium">{t("Condições do Mar", "Sea Conditions")} - Apúlia</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-white/50 text-xs mb-1">
                    <ArrowUp className="h-3 w-3" /> {t("Ondas", "Waves")}
                  </div>
                  <div className="text-xl font-bold text-white">{seaConditions.wave_height?.toFixed(1) || "--"}m</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-white/60 text-xs mb-1">
                    <Clock className="h-3 w-3" /> {t("Período", "Period")}
                  </div>
                  <div className="text-xl font-bold text-white">{seaConditions.wave_period?.toFixed(0) || "--"}s</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-white/60 text-xs mb-1">
                    <Wind className="h-3 w-3" /> {t("Vento", "Wind")}
                  </div>
                  <div className="text-xl font-bold text-white">{seaConditions.wind_speed?.toFixed(0) || "--"}km/h</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-white/60 text-xs mb-1">
                    <Thermometer className="h-3 w-3" /> {t("Água", "Water")}
                  </div>
                  <div className="text-xl font-bold text-white">{seaConditions.water_temp?.toFixed(0) || "--"}°C</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Lessons - After Hero */}
      {upcomingLessons.length > 0 && (
        <section id="lessons" className="py-16 sm:py-20 bg-[#f8f7f4]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <Badge className="mb-3 bg-[#6697a4]/10 text-[#6697a4] border-0">{t("Próximas Aulas", "Upcoming Lessons")}</Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#3d5a63]">
                {t("Reserva já a tua aula", "Book your lesson now")}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingLessons.slice(0, 3).map(lesson => {
                const catLabel = lesson.lesson_category === "surf" ? "Surf" : lesson.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard";
                return (
                  <Card key={lesson.lesson_id} className="bg-white border-0 shadow-sm hover:shadow-lg transition-all rounded-2xl overflow-hidden">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="bg-[#6697a4]/10 text-[#6697a4] border-0 text-xs">{catLabel}</Badge>
                        <Badge variant="outline" className="text-xs">{lesson.lesson_type === "private" ? t("Privada", "Private") : t("Grupo", "Group")}</Badge>
                      </div>
                      <div className="space-y-2 text-sm text-[#475569]">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-[#6697a4] shrink-0" />
                          <span>{new Date(lesson.date + 'T00:00:00').toLocaleDateString(language === "pt" ? 'pt-PT' : 'en-US', { weekday: 'long', day: 'numeric', month: 'short' })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-[#6697a4] shrink-0" />
                          <span>{lesson.start_time} - {lesson.end_time}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t">
                        <div>
                          <span className="text-2xl font-bold text-[#6697a4]">{lesson.price}€</span>
                          <span className="text-xs text-[#94A3B8] ml-2">{lesson.current_enrollments}/{lesson.max_students}</span>
                        </div>
                        <Button onClick={handleGetStarted} size="sm" className="bg-[#6697a4] hover:bg-[#3d5a63] text-white rounded-full px-5">
                          {t("Reservar", "Book")}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Photo Banner */}
      <div className="relative h-40 sm:h-56 overflow-hidden">
        <img src={images.gallery2} alt="Surfing" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#3d5a63]/60 to-transparent flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <p className="text-white text-xl sm:text-2xl font-medium max-w-md">{t("Vive a experiência do surf na melhor praia do norte", "Experience surfing at the best beach in the north")}</p>
          </div>
        </div>
      </div>

      {/* Pricing Section with Subscription Emphasis - Using DB data */}
      <section id="pricing" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <Badge className="mb-3 bg-[#6697a4]/10 text-[#6697a4] border-0">{t("Preços", "Pricing")}</Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#3d5a63]">{t("Planos e Preços", "Plans & Pricing")}</h2>
          </div>

          {/* Monthly Subscription - Carousel (from DB mensalidades) - Only show if there are visible mensalidades */}
          {services.mensalidades && services.mensalidades.length > 0 && (() => {
            const sortedMensalidades = [...services.mensalidades].sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
            const hasMultiple = sortedMensalidades.length > 1;
            const safeIndex = mensalidadeIndex % sortedMensalidades.length;
            const subscription = sortedMensalidades[safeIndex];
            return (
              <div className="mb-12 relative">
                <Card className="bg-gradient-to-r from-[#6697a4] to-[#3d5a63] text-white border-0 shadow-xl rounded-3xl overflow-hidden relative">
                  <CardContent className="p-8 sm:p-12 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div>
                        <Badge className="bg-white/20 text-white border-0 mb-4">{t("Recomendado", "Recommended")}</Badge>
                        <h3 className="text-3xl sm:text-4xl font-bold mb-4">{subscription.name}</h3>
                        <p className="text-white/80 text-lg mb-6">
                          {subscription.description || t("A melhor forma de progredir! Com a nossa mensalidade, tens acesso a aulas regulares por um preço fixo mensal.", 
                             "The best way to progress! With our monthly plan, you get access to regular lessons for a fixed monthly price.")}
                        </p>
                        <ul className="space-y-3 mb-8">
                          {[
                            t(`${subscription.weekly_credits || 2} créditos de aula por semana`, `${subscription.weekly_credits || 2} lesson credits per week`),
                            t("Material incluído em todas as aulas", "Equipment included in all lessons"),
                            t("Renovação automática mensal", "Automatic monthly renewal"),
                            t("Cancela a qualquer momento sem compromisso", "Cancel anytime without commitment"),
                            t("Acesso prioritário às aulas", "Priority access to lessons"),
                          ].map((item, i) => (
                            <li key={`subscription-feature-${i}`} className="flex items-center gap-3">
                              <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                                <Check className="h-4 w-4" />
                              </div>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="text-center lg:text-right">
                        <div className="inline-block bg-white/10 backdrop-blur rounded-2xl p-8">
                          <div className="text-white/60 text-sm mb-2">{t("A partir de", "Starting from")}</div>
                          <div className="text-6xl font-bold mb-2">{subscription.price}€</div>
                          <div className="text-white/60 mb-6">/{t("mês", "month")}</div>
                          <Button onClick={handleGetStarted} size="lg" className="bg-white text-[#3d5a63] hover:bg-white/90 rounded-full px-8 font-semibold">
                            {t("Subscrever Agora", "Subscribe Now")} <ChevronRight className="ml-2 h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    {/* Carousel controls */}
                    {hasMultiple && (
                      <div className="flex items-center justify-center gap-4 mt-6">
                        <button onClick={() => setMensalidadeIndex((safeIndex - 1 + sortedMensalidades.length) % sortedMensalidades.length)} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" data-testid="mensalidade-prev">
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <div className="flex gap-2">
                          {sortedMensalidades.map((_, i) => (
                            <button key={`mensalidade-dot-${i}`} onClick={() => setMensalidadeIndex(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === safeIndex ? "bg-white w-6" : "bg-white/40 hover:bg-white/60"}`} data-testid={`mensalidade-dot-${i}`} />
                          ))}
                        </div>
                        <button onClick={() => setMensalidadeIndex((safeIndex + 1) % sortedMensalidades.length)} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors" data-testid="mensalidade-next">
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Other Pricing Options from DB - Show ALL packs */}
          {services.packs && services.packs.length > 0 && (
            <div className="mb-12">
              <div className={`grid gap-6 ${services.packs.length === 1 ? 'max-w-md mx-auto' : services.packs.length === 2 ? 'sm:grid-cols-2 max-w-2xl mx-auto' : services.packs.length === 3 ? 'sm:grid-cols-3' : services.packs.length === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
                {services.packs.map((pack, idx) => {
                  const isPopular = pack.popular;
                  const isPrivate = pack.service_type === "privado";
                  const pricePerLesson = pack.credits > 1 ? (pack.price / pack.credits).toFixed(0) : null;
                  
                  return (
                    <Card 
                      key={pack.pack_id || idx} 
                      className={`bg-[#f8f7f4] border-0 shadow-sm rounded-2xl relative transition-all hover:shadow-lg ${isPopular ? 'ring-2 ring-[#e8927c]' : ''}`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#e8927c] text-white text-xs px-4 py-1 rounded-full font-medium z-10">
                          {t("Mais Popular", "Most Popular")}
                        </div>
                      )}
                      <CardContent className="p-6 text-center">
                        <h3 className="font-bold text-lg text-[#3d5a63] mb-2">{pack.name}</h3>
                        <p className="text-sm text-[#94A3B8] mb-4 min-h-[40px]">
                          {pack.description || (isPrivate ? t("Atenção exclusiva", "Exclusive attention") : t("Experimenta sem compromisso", "Try with no commitment"))}
                        </p>
                        <div className={`text-4xl font-bold mb-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`}>
                          {pack.price}€
                        </div>
                        <ul className="space-y-2 text-sm text-[#475569] mb-6 text-left">
                          {pack.credits > 1 ? (
                            <>
                              <li className="flex items-center gap-2">
                                <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                {t(`${pack.credits} aulas incluídas`, `${pack.credits} lessons included`)}
                              </li>
                              {pricePerLesson && (
                                <li className="flex items-center gap-2">
                                  <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                  {t(`${pricePerLesson}€ por aula`, `${pricePerLesson}€ per lesson`)}
                                </li>
                              )}
                              <li className="flex items-center gap-2">
                                <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                {t("Válido por 1 ano", "Valid for 1 year")}
                              </li>
                            </>
                          ) : isPrivate ? (
                            <>
                              <li className="flex items-center gap-2">
                                <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                {t("Instrutor dedicado", "Dedicated instructor")}
                              </li>
                              <li className="flex items-center gap-2">
                                <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                {t("Horário flexível", "Flexible schedule")}
                              </li>
                              <li className="flex items-center gap-2">
                                <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                {t("Evolução rápida", "Fast progress")}
                              </li>
                            </>
                          ) : (
                            <>
                              <li className="flex items-center gap-2">
                                <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                {t("2 horas de aula", "2 hour lesson")}
                              </li>
                              <li className="flex items-center gap-2">
                                <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                {t("Material incluído", "Equipment included")}
                              </li>
                              <li className="flex items-center gap-2">
                                <Check className={`h-4 w-4 ${isPopular ? 'text-[#e8927c]' : 'text-[#6697a4]'}`} />
                                {t("Seguro incluído", "Insurance included")}
                              </li>
                            </>
                          )}
                        </ul>
                        <Button 
                          onClick={handleGetStarted} 
                          className={`w-full rounded-full ${isPopular ? 'bg-[#e8927c] hover:bg-[#d87c66]' : 'border-[#6697a4] text-[#6697a4] hover:bg-[#6697a4] hover:text-white'}`}
                          variant={isPopular ? "default" : "outline"}
                        >
                          {pack.credits > 1 ? t("Comprar Pack", "Buy Pack") : t("Reservar", "Book")}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rentals */}
          <div className="bg-[#f8f7f4] rounded-2xl p-6 sm:p-8">
            <h3 className="text-xl font-bold text-[#3d5a63] mb-6 text-center">{t("Aluguer de Material", "Equipment Rental")}</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {(c.rentals || []).map((rental, idx) => (
                <div key={rental.id || idx} className="bg-white rounded-xl p-5 text-center hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 rounded-full bg-[#6697a4]/10 flex items-center justify-center mx-auto mb-3">
                    <Waves className="h-7 w-7 text-[#6697a4]" />
                  </div>
                  <h4 className="font-bold text-[#3d5a63] mb-1">{rental[`name_${language}`] || rental.name_pt}</h4>
                  <p className="text-sm text-[#94A3B8] mb-3">{rental[`desc_${language}`] || rental.desc_pt}</p>
                  <div className="text-2xl font-bold text-[#6697a4]">{rental.price}€<span className="text-sm font-normal text-[#94A3B8]">/{rental.duration}</span></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-24 bg-[#3d5a63] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-white/10 text-white border-0">{t("Sobre Nós", "About Us")}</Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">{c.about?.[`title_${language}`] || c.about?.title_pt}</h2>
              <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-4">{c.about?.[`text1_${language}`] || c.about?.text1_pt}</p>
              <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-8">{c.about?.[`text2_${language}`] || c.about?.text2_pt}</p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Award, text: t("Instrutores Certificados", "Certified Instructors") },
                  { icon: Shield, text: t("Seguro Incluído", "Insurance Included") },
                  { icon: Heart, text: t("Equipamento Premium", "Premium Equipment") },
                  { icon: Waves, text: t("Grupos Pequenos", "Small Groups") },
                ].map((item, idx) => (
                  <div key={`about-feature-${idx}`} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-[#e8927c]" />
                    </div>
                    <span className="text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative pb-8">
              {/* Photo Grid with Float Effect */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <img src={images.about} alt="About" className="animate-float-slow rounded-2xl shadow-xl w-full h-48 object-cover" />
                </div>
                <div>
                  <img src={images.gallery1} alt="Surf" className="animate-float rounded-xl shadow-lg w-full h-32 object-cover" />
                </div>
                <div>
                  <img src={images.gallery3} alt="Lesson" className="animate-float-delay rounded-xl shadow-lg w-full h-32 object-cover" />
                </div>
              </div>
              <div className="absolute -bottom-2 -left-6 bg-[#e8927c] rounded-2xl p-6 shadow-xl animate-float">
                <div className="text-4xl font-bold">15+</div>
                <div className="text-sm opacity-80">{t("Anos de Experiência", "Years Experience")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery with Instagram */}
      <section className="py-0 overflow-hidden">
        <div className="grid grid-cols-2 md:grid-cols-4">
          {[images.gallery1, images.gallery2, images.gallery3, images.gallery4].map((img, idx) => (
            <div key={`gallery-image-${idx}`} className="relative h-48 sm:h-64 md:h-80 overflow-hidden group">
              <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-[#6697a4]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-center justify-center">
                <a href="https://www.instagram.com/apuliasurfschool/" target="_blank" rel="noopener noreferrer" className="text-white font-medium flex items-center gap-2">
                  <Instagram className="h-5 w-5" /> @apuliasurfschool
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Carousel */}
      {testimonials.length > 0 && (
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-[#f8f7f4] px-4 py-2 rounded-full mb-4">
                <svg className="h-5 w-5 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                <span className="font-bold text-[#3d5a63]">{googleRating}</span>
                <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`h-4 w-4 ${s <= Math.round(googleRating) ? "text-[#F59E0B] fill-current" : "text-gray-200"}`} />)}</div>
                <span className="text-sm text-[#94A3B8]">({googleReviewCount})</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#3d5a63]">{t("O que dizem sobre nós", "What people say")}</h2>
            </div>
            
            {/* Carousel Container */}
            <div className="relative">
              {/* Navigation Buttons */}
              <button 
                onClick={() => setTestimonialIndex(prev => prev === 0 ? testimonials.length - 1 : prev - 1)}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors hidden sm:flex"
              >
                <ChevronLeft className="h-5 w-5 text-[#3d5a63]" />
              </button>
              <button 
                onClick={() => setTestimonialIndex(prev => prev === testimonials.length - 1 ? 0 : prev + 1)}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors hidden sm:flex"
              >
                <ChevronRight className="h-5 w-5 text-[#3d5a63]" />
              </button>
              
              {/* Cards */}
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${testimonialIndex * 100}%)` }}
                >
                  {testimonials.map((review, idx) => (
                    <div key={review.testimonial_id || `testimonial-${idx}`} className="w-full flex-shrink-0 px-2 sm:px-4">
                      <Card className="bg-[#f8f7f4] border-0 rounded-2xl max-w-2xl mx-auto">
                        <CardContent className="p-8 text-center">
                          <div className="flex justify-center gap-1 mb-4">
                            {[1,2,3,4,5].map(s => (
                              <Star key={`star-${review.testimonial_id || idx}-${s}`} className={`h-5 w-5 ${s <= (review.rating || 5) ? "text-[#F59E0B] fill-current" : "text-gray-200"}`} />
                            ))}
                          </div>
                          <p className="text-[#475569] text-lg mb-6 italic">"{review.text}"</p>
                          <div className="flex items-center justify-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-[#6697a4] flex items-center justify-center text-white font-bold text-lg">
                              {review.author_name?.charAt(0) || "?"}
                            </div>
                            <div className="text-left">
                              <p className="font-semibold text-[#3d5a63]">{review.author_name}</p>
                              <p className="text-sm text-[#94A3B8]">Google Review</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((review, idx) => (
                  <button
                    key={review.testimonial_id || `testimonial-dot-${idx}`}
                    onClick={() => setTestimonialIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${idx === testimonialIndex ? "bg-[#6697a4] w-6" : "bg-[#6697a4]/30"}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-24 bg-[#3d5a63] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <Badge className="mb-4 bg-white/10 text-white border-0">{t("Contacto", "Contact")}</Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">{t("Vem visitar-nos!", "Come visit us!")}</h2>
              <p className="text-white/80 mb-8 text-base sm:text-lg">{t("Estamos na Praia da Apúlia, uma das melhores praias para surf no norte de Portugal.", "We are at Praia da Apúlia, one of the best beaches for surfing in northern Portugal.")}</p>
              
              <div className="space-y-4">
                <a href={`tel:${c.contact?.phone?.replace(/\s/g, "")}`} className="flex items-center gap-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-[#e8927c] flex items-center justify-center"><Phone className="h-6 w-6" /></div>
                  <div><p className="text-sm text-white/60">{t("Telefone", "Phone")}</p><p className="font-medium text-lg">{c.contact?.phone}</p></div>
                </a>
                <a href={`mailto:${c.contact?.email}`} className="flex items-center gap-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-[#6697a4] flex items-center justify-center"><Mail className="h-6 w-6" /></div>
                  <div><p className="text-sm text-white/60">Email</p><p className="font-medium">{c.contact?.email}</p></div>
                </a>
                <a href={c.contact?.address_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.contact?.address || "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center"><MapPin className="h-6 w-6" /></div>
                  <div><p className="text-sm text-white/60">{t("Morada", "Address")}</p><p className="font-medium">{c.contact?.address}</p></div>
                </a>
              </div>

              <div className="flex gap-4 mt-8">
                <a href="https://www.instagram.com/apuliasurfschool/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><Instagram className="h-6 w-6" /></a>
                <a href="https://www.facebook.com/apuliasurfschool" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"><Facebook className="h-6 w-6" /></a>
                {c.contact?.whatsapp && (
                  <a href={`https://wa.me/${c.contact.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-[#25D366] flex items-center justify-center hover:opacity-80 transition-opacity"><MessageCircle className="h-6 w-6" /></a>
                )}
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden h-80 lg:h-auto">
              <iframe
                src={getGoogleMapsEmbedUrl(c.contact?.address_maps_url, c.contact?.address)}
                width="100%" height="100%" style={{ border: 0, minHeight: 300 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="grayscale"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Conditions */}
      <footer className="bg-[#2d444b] text-white/60">
        {/* Compact Conditions */}
        <div className="border-b border-white/10 py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
              <div>
                <p className="text-white/80 font-medium text-sm mb-2">{t("Informações Importantes", "Important Information")}</p>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm text-white/50">
                  {(c.conditions?.[`items_${language}`] || c.conditions?.items_pt || []).slice(0, 4).map((item, idx) => (
                    <li key={`footer-condition-${idx}`}>• {item}</li>
                  ))}
                </ul>
              </div>
              <div className="md:text-right">
                <p className="text-white/50 text-sm">{t("Métodos de Pagamento", "Payment Methods")}</p>
                <p className="text-white/70 text-sm">{c.conditions?.[`payment_methods_${language}`] || c.conditions?.payment_methods_pt}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Main Footer */}
        <div className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* White Logo */}
              <img src="/assets/images/logo.png" alt="Apúlia Surf School" className="h-8 w-auto brightness-0 invert opacity-80" />
              <div className="text-center">
                <p className="text-sm">© {new Date().getFullYear()} Apúlia Surf School. {t("Todos os direitos reservados.", "All rights reserved.")}</p>
                <p className="text-xs mt-1">
                  {t("Desenvolvido por", "Developed by")} <a href="https://johnmaia.dev" target="_blank" rel="noopener noreferrer" className="text-[#e8927c] hover:text-white transition-colors">johnmaia.dev</a>
                </p>
              </div>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/apuliasurfschool/" target="_blank" rel="noopener noreferrer"><Instagram className="h-5 w-5 hover:text-white transition-colors" /></a>
                <a href="https://www.facebook.com/apuliasurfschool" target="_blank" rel="noopener noreferrer"><Facebook className="h-5 w-5 hover:text-white transition-colors" /></a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-[#3d5a63]">
              {magicLinkSent ? t("Verifica o teu email", "Check your email") : t("Entrar / Registar", "Login / Register")}
            </DialogTitle>
          </DialogHeader>
          {!magicLinkSent ? (
            <form onSubmit={handleMagicLink} className="space-y-4 pt-2">
              <div className="space-y-2">
                <label className="text-sm text-[#475569]">Email</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="o.teu@email.com" required />
              </div>
              <Button type="submit" className="w-full bg-[#6697a4] hover:bg-[#3d5a63]" disabled={authLoading}>
                {authLoading ? t("A enviar...", "Sending...") : t("Enviar Magic Link", "Send Magic Link")}
              </Button>
              <p className="text-xs text-center text-[#94A3B8]">{t("Vais receber um link no email para aceder", "You'll receive a link in your email to access")}</p>
            </form>
          ) : (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 bg-[#10B981]/10 rounded-full flex items-center justify-center mx-auto">
                <Mail className="h-8 w-8 text-[#10B981]" />
              </div>
              <p className="text-[#475569]">{t("Enviámos um link para", "We sent a link to")} <strong>{email}</strong></p>
              {debugLink && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-700 mb-2">🔧 Debug:</p>
                  <a href={debugLink} className="text-xs text-[#6697a4] underline break-all">{t("Clica aqui", "Click here")}</a>
                </div>
              )}
              <Button variant="outline" onClick={() => { setMagicLinkSent(false); setDebugLink(null); }} className="w-full">
                {t("Usar outro email", "Use another email")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
